import React, { useState, useEffect } from 'react';
import { addMonths, subMonths } from 'date-fns';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarHeader } from '../components/CalendarHeader';
import { MonthView } from '../components/MonthView';
import { Meeting } from '../../../shared/types';
import { MeetingModal, Tab } from '../../../features/meetings/components/MeetingModal';
import { useAuth } from '../../../context/AuthContext';
import { Loader2 } from 'lucide-react';

// Define props for CalendarPage
interface CalendarPageProps {
  meetings: Meeting[];
  account: string | null;
  loading: boolean; // This will be loadingInitial from App.tsx
  error: string | null;
  handleMeetingUpdate: (meetingId: string, fields: Partial<Record<string, unknown>>) => Promise<void>;
}

export const CalendarPage: React.FC<CalendarPageProps> = ({
  meetings,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  account,
  loading,
  error,
  handleMeetingUpdate
}) => {
  const { t } = useTranslation();
  const { user } = useAuth(); // Still need user from useAuth for the initial auth check / redirect

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('contact'); // Tab type was 'contact' | 'conversation' | 'notes'

  // Sync selectedMeeting with updated meetings data to show immediate UI updates
  useEffect(() => {
    if (selectedMeeting) {
      const updatedMeeting = meetings.find(meeting => meeting.id === selectedMeeting.id);
      if (updatedMeeting && updatedMeeting !== selectedMeeting) {
        setSelectedMeeting(updatedMeeting);
      }
    }
  }, [meetings, selectedMeeting]);

  if (!user) { // Auth check before rendering content
    return <Navigate to="/login" replace />;
  }

  const handlePrevMonth = () => {
    const newDate = subMonths(currentDate, 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = addMonths(currentDate, 1);
    setCurrentDate(newDate);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {loading && meetings.length === 0 ? ( // Use loading prop, show if no meetings yet
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-[#0055FF]" />
          <span className="ml-2 text-sm text-gray-500">{t('meetings.loadingMeetings')}</span>
        </div>
      ) : error ? (
        <div className="text-center py-12 px-8">
          <p className="text-sm text-red-600 font-medium mb-2">{error}</p>
          <button
            onClick={() => window.location.reload()} // Or a more specific refresh action
            className="text-sm text-[#0055FF] hover:text-[#0044CC]"
          >
            {t('meetings.retryAgain')}
          </button>
        </div>
      ) : (
        <>
          <CalendarHeader
            currentDate={currentDate}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            view={view}
            onViewChange={setView}
            meetings={meetings} // Pass meetings to header if it uses them
          />

          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
            <MonthView
              currentDate={currentDate}
              meetings={meetings}
              onMeetingClick={(meeting) => {
                setSelectedMeeting(meeting);
              }}
            />
          </main>
        </>
      )}

      {selectedMeeting && (
        <MeetingModal
          meeting={selectedMeeting}
          onClose={() => {
            setSelectedMeeting(null);
          }}
          onStatusUpdate={async (status) => {
            await handleMeetingUpdate(selectedMeeting.id, { 'Status Lead': status });
          }}
          onDateUpdate={async (date) => {
            await handleMeetingUpdate(selectedMeeting.id, { 'Fecha Reunión': date });
          }}
          onContactUpdate={async (fieldsToUpdate) => { // Renamed to avoid conflict
            await handleMeetingUpdate(selectedMeeting.id, fieldsToUpdate);
          }}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onNotesUpdate={async (notes) => {
            await handleMeetingUpdate(selectedMeeting.id, { Notas: notes });
          }}
          isOpen={!!selectedMeeting}
        />
      )}
    </div>
  );
};